import {
  CHAT_TEXT_EXTRACTION_WARNING_I18N_KEYS,
  KNOWLEDGE_TEXT_EXTRACTION_WARNING_I18N_KEYS,
} from "../constants/text-extraction";
import type {
  IMarkTextExtractionWarningsProps,
  IMessageFileAttachment,
} from "../types/chat";
import {
  ETextExtractionWarningCode,
  type IGetTextExtractionWarningTextProps,
  type IIsSameTextExtractionWarningProps,
  type ITextExtractionWarning,
  type ITextExtractionWarningText,
  type TChatTextExtractionWarningI18nKey,
  type TKnowledgeTextExtractionWarningI18nKey,
  type TTextExtractionWarningValues,
} from "../types/text-extraction";
import { formatNumber } from "./number";

// NOTE: NSAI-7345 — the code a warning is worded by, or null for one this client
// does not know (a newer server's). The page-split sentence needs the split;
// without it the warning reads by its totals, as the server's note to the
// model does.
const resolveWordingCode = (
  warning: ITextExtractionWarning,
): ETextExtractionWarningCode | null => {
  if (warning.code === ETextExtractionWarningCode.PAGES_WITHOUT_TEXT) {
    return warning.lowTextPageCount === null
      ? ETextExtractionWarningCode.LOW_TEXT
      : ETextExtractionWarningCode.PAGES_WITHOUT_TEXT;
  }
  return warning.code === ETextExtractionWarningCode.LOW_TEXT
    ? ETextExtractionWarningCode.LOW_TEXT
    : null;
};

const formatWarningValues = (
  props: IGetTextExtractionWarningTextProps,
): TTextExtractionWarningValues => {
  const { warning, language } = props;
  return {
    pageCount: formatNumber({ value: warning.pageCount, language }),
    textCharCount: formatNumber({ value: warning.textCharCount, language }),
    lowTextPageCount: formatNumber({
      value: warning.lowTextPageCount ?? 0,
      language,
    }),
  };
};

// NOTE: NSAI-7345 — how the chat words a warning under a message's attachments
// (the sentence names the file); null for a code this client does not know.
export const getChatTextExtractionWarningText = (
  props: IGetTextExtractionWarningTextProps,
): ITextExtractionWarningText<TChatTextExtractionWarningI18nKey> | null => {
  const code = resolveWordingCode(props.warning);
  return code === null
    ? null
    : {
        key: CHAT_TEXT_EXTRACTION_WARNING_I18N_KEYS[code],
        values: formatWarningValues(props),
      };
};

// NOTE: NSAI-7345 — how the Knowledge lists word a warning on a file's row;
// null for a code this client does not know.
export const getKnowledgeTextExtractionWarningText = (
  props: IGetTextExtractionWarningTextProps,
): ITextExtractionWarningText<TKnowledgeTextExtractionWarningI18nKey> | null => {
  const code = resolveWordingCode(props.warning);
  return code === null
    ? null
    : {
        key: KNOWLEDGE_TEXT_EXTRACTION_WARNING_I18N_KEYS[code],
        values: formatWarningValues(props),
      };
};

// NOTE: NSAI-7345 — the attachments of a message the chat warns about, in the
// order they were attached.
export const getTextExtractionWarnedFiles = (
  files: readonly IMessageFileAttachment[] | undefined,
): IMessageFileAttachment[] =>
  (files ?? []).filter((file) => file.textExtractionWarning !== undefined);

const isSameWarning = ({
  current,
  next,
}: IIsSameTextExtractionWarningProps): boolean =>
  current !== undefined &&
  current.code === next.code &&
  current.pageCount === next.pageCount &&
  current.textCharCount === next.textCharCount &&
  current.lowTextPageCount === next.lowTextPageCount;

// NOTE: NSAI-7345 — sets the warning on the named attachments of a message.
// Written as an updateMessageInChatHistory mutator for the cloned message:
// returns whether anything changed, so a replayed event leaves the cache (and
// every memoized row) untouched.
export const markTextExtractionWarnings = (
  props: IMarkTextExtractionWarningsProps,
): boolean => {
  const { message, files } = props;
  const warningByFileUuid = new Map(
    files.map((file) => [file.fileUuid, file.warning]),
  );
  let isChanged = false;

  message.files = message.files.map((file) => {
    const warning = warningByFileUuid.get(file.uuid);
    if (
      warning === undefined ||
      isSameWarning({ current: file.textExtractionWarning, next: warning })
    ) {
      return file;
    }
    isChanged = true;
    return { ...file, textExtractionWarning: warning };
  });

  return isChanged;
};
